import React, { Fragment, useEffect, useState } from "react";
import { Col, Form, Input, Row, Select, DatePicker, Space, Divider, Upload, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { UploadOutlined } from "@ant-design/icons";

import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import ButtonComponent from "@/components/button/Button";
import BackWithLabel from "@/components/common/BackWithLabel";

import { countryList } from "@/helpers/countryList";
import {
  setCurrentForm,
  setFormProps,
  setModalOpen,
  setUserInfo,
} from "@/reducers/commonSlice";

import { getUserBasicInfo } from "@/services/users/getUserBasicInfo";
import { updateUserProfile } from "@/services/users/updateUserProfile";
import { getUserProfile } from "@/services/users/getUserProfile";
import { getUserIdentity } from "@/services/users/getUserIdentity";
import { verifyPrimaryContact } from "@/services/accounts/verifyPrimaryContact";

import IconAdd from "@/components/icons/IconAdd";
import IconDelete from "@/components/icons/IconDelete";

import "@/styles/pages/profile/basic-contact-information.scss";
import { updateFile } from "@/services/users/updateFile";
import useTriggerNotification from "@/hooks/useTriggerNotification";
import { uploadFile } from "@/services/users/uploadFile";
import { getAllCountriesDetails } from "@/services/locations/getAllCountriesDetails";
import { convertToDate, dateFormatList, keyFilterOption } from "@/helpers";
import moment from "moment";
import { getAllLanguages } from "@/services/common/getAllLanguages";
import IconCross from "@/components/icons/IconCross";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import getCountryISO3 from "@/lib/data/isoTwoToThree";
import getCountryISO2 from "@/lib/data/isoThreeToTwo";

const formItemLayout = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 24, offset: 0 },
  },
};

const genderOptions = [
  {
    value: "male",
    label: "Male",
  },
  {
    value: "female",
    label: "Female",
  },
  {
    value: "other",
    label: "Other",
  },
];

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const { Option } = Select;

const BasicProfileInformation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [passportform] = Form.useForm();
  const { trigger, element } = useTriggerNotification();

  const [isPrimaryEmailVarified, setIsPrimaryEmailVarified] = useState();
  const [isPrimaryEmailDisabled, setIsPrimaryEmailDisabled] = useState(true);
  const [isPrimaryEmailChange, setIsPrimaryEmailChange] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState("");

  const [isPrimaryMobileNumberVerified, setIsPrimaryMobileNumberVerified] = useState();
  const [isPrimaryMobileNumberDisabled, setIsPrimaryMobileNumberDisabled] = useState(true);
  const [primaryMobileNumber, setPrimaryMobileNumber] = useState("");

  const [countryData, setCountryData] = useState([]);
  const [launguageList, setLaunguageList] = useState([]);
  const [isFormDisabled, setIsFormDisabled] = useState(true);

  const [profileImage, setProfileImage] = useState();
  const [profileImageBinary, setProfileImageBinary] = useState();
  const [profileImageId, setProfileImageId] = useState("");

  const eighteenYearsAgo = dayjs().subtract(18, "year");

  const handleChange = (value, name) => {
    // console.log(`selected ${value} - ${name}`);
  };

  const handleFinish = (value) => {
    const otherContacts = [];

    if (value.emails?.length) {
      otherContacts.push(...value.emails.map((email) => ({ email: email })));
    }

    if (value.mobiles?.length) {
      otherContacts.push(
        ...value.mobiles.map((x) => ({
          mobileNumber: x.countryCode ? x.countryCode + " " + x.number : "+91 " + x.number,
        }))
      );
    }

    updateUserProfile({
      userName: value.userName,
      gender: value.gender ?? "",
      dateOfBirth: value["dob"] ? value["dob"].format("DD/MM/YYYY") : "",
      countryOfBirth: value.cob ? getCountryISO3(value.cob) : "",
      nationality: value.nationality ? getCountryISO3(value.nationality) : "",
      nativeLanguage: value.nativeLanguage ?? "",
      otherContacts: [...otherContacts],
    })
      .then((res) => {
        console.log(res, "res");
        trigger({
          type: "success",
          message: "Profile Information updated successfully",
        });
        setIsFormDisabled(true);
        setIsPrimaryMobileNumberDisabled(true);
        setIsPrimaryEmailDisabled(true);
        fetchData();
      })
      .catch((error) => {
        trigger({
          type: "error",
          message: error.response?.data?.error,
        });
        console.log(error);
      });

    if (profileImageBinary) {
      const formData = new FormData();
      formData.append("file", profileImageBinary);

      if (profileImageId) {
        updateFile(profileImageId, formData)
          .then((res) => {
            console.log(res, "file res");
          })
          .catch((error) => {
            trigger({
              type: "error",
              message: error.response?.data?.error,
            });
            console.log(error, "file error");
          });
      } else {
        uploadFile(1, formData)
          .then((res) => console.log(res))
          .catch((err) => console.log(err));
      }
    }
  };

  const verifyPrimaryEmailOrMobileNumber = (emailOrMobileNumber, varient) => {
    const data =
      varient === "email" ? { email: emailOrMobileNumber } : { mobileNumber: emailOrMobileNumber };
    verifyPrimaryContact(data)
      .then((response) => {
        console.log("OTP :: ", response);
        dispatch(setModalOpen(true));
        dispatch(setCurrentForm("verifyPrimaryEmailOrPassword"));
        dispatch(
          setFormProps({
            data,
            onSubmit: () => fetchData(),
          })
        );
      })
      .catch((error) => {
        trigger({
          type: "error",
          message: error.response?.data?.error,
        });
      });
  };

  const fetchData = () => {
    getUserBasicInfo()
      .then((res) => {
        console.log(res, "res");
        dispatch(setUserInfo(res));
        setProfileImage(res?.profileImgUrl);
        setProfileImageId(res?.profileImgFileId);

        if (res.user?.email === res.verified?.email) {
          setIsPrimaryEmailVarified(true);
          setIsPrimaryEmailDisabled(true);
          setPrimaryEmail(res.verified.email);
        } else {
          setIsPrimaryEmailVarified(false);
          setPrimaryEmail(res.user.email);
        }

        // if (res.verified.email) {
        //   setIsPrimaryEmailVarified(true);
        //   setIsPrimaryEmailDisabled(true);
        //   setPrimaryEmail(res.verified.email);
        // }
        // if (res.toVerify.email) {
        //   setIsPrimaryEmailVarified(false);
        //   setPrimaryEmail(res.toVerify.email);
        // }

        if (res.user?.mobileNumber === res.verified?.mobileNumber) {
          setIsPrimaryMobileNumberVerified(true);
          setIsPrimaryMobileNumberDisabled(true);
          setPrimaryMobileNumber(res.verified.mobileNumber);
        } else {
          setIsPrimaryMobileNumberVerified(false);
          setPrimaryMobileNumber(res.user.mobileNumber);
        }

        // if (res.verified.mobileNumber) {
        //   setIsPrimaryMobileNumberVerified(true);
        //   setIsPrimaryMobileNumberDisabled(true);
        //   setPrimaryMobileNumber(res.verified.mobileNumber);
        // }

        // if (res.toVerify.mobileNumber) {
        //   setIsPrimaryMobileNumberVerified(false);
        //   setPrimaryMobileNumber(res.toVerify.mobileNumber);
        // }

        // if (!res.verified.email && !res.toVerify.email) {
        //   setIsPrimaryEmailVarified(false);
        //   setPrimaryEmail(res.user.email);
        // }

        // if (!res.verified.mobileNumber && !res.toVerify.mobileNumber) {
        //   setIsPrimaryMobileNumberVerified(false);
        //   setPrimaryMobileNumber(res.verified.mobileNumber);
        // }

        getUserProfile()
          .then((response) => {
            const mobileNumber = res.user?.mobileNumber?.split(" ");
            const otherDetails = response.otherContacts;
            let otherEmail = [];
            let otherNumber = [];

            // Iterate through the array and extract email and mobileNumber
            otherDetails?.forEach((item) => {
              if (item.email) {
                otherEmail.push(item.email);
              }
              if (item.mobileNumber) {
                const number = item.mobileNumber.split(" ");
                otherNumber.push({ countryCode: number[0], number: number[1] });
              }
            });

            form.setFieldsValue({
              email: res.user.email,
              userName: res.user.userName,
              firstName: res.user.firstName,
              lastName: res.user.lastName,
              countryCode: mobileNumber ? mobileNumber[0] : "",
              number: mobileNumber ? mobileNumber[1] : "",
              gender: response.gender ?? null,
              dob: response.dateOfBirth
                ? convertToDate(response.dateOfBirth) // Use the existing date if available
                : // : eighteenYearsAgo, // Default to 18 years ago if no date is provided
                  undefined, // Default to 18 years ago if no date is provided
              cob: getCountryISO2(response.countryOfBirth),
              nationality: getCountryISO2(response.nationality),
              nativeLanguage: response.nativeLanguage,
              emails: otherEmail,
              mobiles: otherNumber,
            });
          })
          .catch((error) => {
            console.log(error);
          });

        getAllLanguages()
          .then((res) => {
            setLaunguageList(
              res.map((x) => ({
                label: x.name,
                value: x.id,
              }))
            );
          })
          .catch((err) => console.log(err));
      })
      .catch((error) => {
        console.log(error);
      });

    getUserIdentity()
      .then((res) => {
        const userData = res[0] || {};
        passportform.setFieldsValue({
          passportName: userData.nameInDocument ?? "",
          passportIssueLocation: userData.issuingLocation ?? null,
          passportNumber: userData.documentNo ?? "",
          passportIssueDate: userData.issuedDate ? convertToDate(userData.issuedDate) : null,
          passportExpiryDate: userData.expiryDate ? convertToDate(userData.expiryDate) : null,
        });
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchData();
    setTimeout(() => {
      fetchData();
    }, 200);
  }, []);

  const handleImageChange = async ({ file }) => {
    if (file.status === "done" || file.status === "uploading") {
      const base64 = await getBase64(file.originFileObj);
      setProfileImage(base64);
      setProfileImageBinary(file.originFileObj); // Store the binary image
    } else if (file.status === "removed") {
      setProfileImage(null);
      setProfileImageBinary(null); // Store the binary image
    }
  };

  const handleMobileNumberChange = (value, option) => {
    const countryCode = option?.value ?? "+91";
    console.log("countryCode :: ", countryCode);
    setPrimaryMobileNumber(`${countryCode} ${value}`);
  };

  const disabledDate = (current) => {
    // Disable dates after today but allow today
    return current && current > moment().subtract(18, "years");
  };

  useEffect(() => {
    getAllCountriesDetails().then((res) => setCountryData(res));
  }, []);

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return label?.length ? (
      <div
        className={isFormDisabled ? "disabled selected-chip" : "selected-chip"}
        onMouseDown={onPreventMouseDown}
      >
        <span>{label}</span>{" "}
        <IconCross
          style={{ cursor: isFormDisabled ? "not-allowed" : "pointer" }}
          color={isFormDisabled ? "#6d6e75" : "#db5800"}
          onClick={onClose}
        />
      </div>
    ) : (
      <></>
    );
  };

  useUnsavedChanges(!isFormDisabled);

  // console.log(dayjs().subtract(18, "year"), "dayjs().");
  return (
    <Fragment>
      {element}
      <div className="basic-contact-information-page">
        <Container>
          <CardComponent>
            <Row>
              <Col xxl={24}>
                <div className="heading">
                  <BackWithLabel
                    label={`${!isFormDisabled ? "Edit" : ""} Basic Profile Information`}
                    path="/profile"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col xxl={4}>
                <div className="profile-picture">
                  <Upload
                    listType="picture-circle"
                    showUploadList={false}
                    onChange={handleImageChange}
                    disabled={isFormDisabled}
                    accept=".jpg, .jpeg, .png, .webp"
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Col>
            </Row>

            <Form
              name="searchGuide"
              initialValues={{ remember: true }}
              layout="vertical"
              onFinish={handleFinish}
              requiredMark={customizeRequiredMark}
              form={form}
              onKeyDown={(e) => (e.key == "Enter" ? e.preventDefault() : "")}
              {...formItemLayoutWithOutLabel}
            >
              <Row gutter={[16, 0]} className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="User Name"
                    name="userName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an username!",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      disabled={isFormDisabled}
                      autoComplete="off"
                      placeholder="Enter your username"
                      onKeyPress={(e) => {
                        // allow only letters, numbers, and underscores
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9_]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");

                        if (!/^[A-Za-z0-9_]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your firstname!",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      disabled={true}
                      autoComplete="off"
                      placeholder="Enter your firstname"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your lastname!",
                      },
                      {
                        pattern: /^[A-Za-z\s]*$/,
                        message: "Last name can only contain letters and spaces",
                      },
                    ]}
                  >
                    <Input
                      disabled={true}
                      autoComplete="off"
                      placeholder="Enter your lastname"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (!/^[A-Za-z\s]+$/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Date of Birth (DD/MM/YYYY)"
                    name="dob"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your date of birth!",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isFormDisabled}
                      placeholder="Select your date of birth"
                      format={dateFormatList}
                      allowClear={true}
                      disabledDate={(current) => current && current > eighteenYearsAgo.endOf("day")}
                      defaultPickerValue={eighteenYearsAgo}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      {
                        type: "email",
                        required: true,
                        message: "Please enter your email!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled || isPrimaryEmailDisabled ? true : false}
                      className={isPrimaryEmailVarified ? `verified-border` : `pending-border`}
                      placeholder="Enter your email"
                      onChange={(e) => setPrimaryEmail(e.target.value)}
                    />
                  </Form.Item>
                  {!isPrimaryEmailVarified && !isPrimaryEmailChange && (
                    <div className="unverified" align="end">
                      {/* Verification Pending!{" "} */}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            verifyPrimaryEmailOrMobileNumber(primaryEmail, "email");
                          }
                        }}
                        className={isFormDisabled ? "disabled" : ""}
                      >
                        Verify Now
                      </span>
                      {"   "}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryEmailDisabled(false);
                            setIsPrimaryEmailChange(true);
                          }
                        }}
                        className={isFormDisabled ? "disabled" : ""}
                      >
                        Change
                      </span>
                    </div>
                  )}
                  {isPrimaryEmailVarified && isPrimaryEmailDisabled && (
                    <div className="verified" align="end">
                      {/* Verified{" "} */}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryEmailDisabled(false);
                          }
                        }}
                        className={isFormDisabled && "disabled"}
                      >
                        Edit Primary Email
                      </span>
                    </div>
                  )}
                  {(isPrimaryEmailVarified || isPrimaryEmailChange) && !isPrimaryEmailDisabled && (
                    <div className="verified" align="end">
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryEmailDisabled(true);
                            setIsPrimaryEmailChange(false);
                          }
                        }}
                      >
                        Cancel
                      </span>{" "}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryEmailDisabled(false);
                            verifyPrimaryEmailOrMobileNumber(primaryEmail, "email");
                          }
                        }}
                      >
                        Verify
                      </span>
                    </div>
                  )}
                  <Form.List name="emails">
                    {(fields, { add, remove }, { errors }) => (
                      <>
                        {fields.map((field, index) => (
                          <Form.Item
                            {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                            key={field.key}
                          >
                            <div className="dynamic-input">
                              <Form.Item
                                {...field}
                                validateTrigger={["onChange", "onBlur"]}
                                rules={[
                                  {
                                    required: true,
                                    whitespace: true,
                                    message: "Please enter email!",
                                  },
                                  {
                                    type: "email",
                                    message: "Enter valid email",
                                  },
                                ]}
                                noStyle
                              >
                                <Input
                                  disabled={isFormDisabled}
                                  autoComplete="off"
                                  placeholder="Enter email address"
                                  style={{
                                    width: isFormDisabled ? "100%" : "90%",
                                  }}
                                />
                              </Form.Item>
                              {fields.length > 0 ? (
                                <Fragment>
                                  {!isFormDisabled && (
                                    <IconDelete
                                      className="dynamic-delete-button"
                                      onClick={() => {
                                        remove(field.name);
                                      }}
                                      color={isFormDisabled ? "#6d6e75" : "#E9635E"}
                                      style={{
                                        border: "1px solid",
                                        borderRadius: "4px",
                                        padding: "4px",
                                        cursor: `${isFormDisabled ? "" : "pointer"}`,
                                      }}
                                    />
                                  )}
                                </Fragment>
                              ) : null}
                            </div>
                          </Form.Item>
                        ))}
                        <Form.Item>
                          <ButtonComponent
                            onClick={() => add()}
                            iconRight={false}
                            disabled={isFormDisabled}
                            label="Add Another Email"
                            style={{
                              width: "100%",
                              height: "2.5rem",
                              padding: "0.5rem 0",
                              border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                            }}
                          >
                            <IconAdd
                              width="24px"
                              height="24px"
                              color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                              style={{
                                border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                borderRadius: "4px",
                              }}
                            />
                          </ButtonComponent>
                          <Form.ErrorList errors={errors} />
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Phone Number">
                    <Space.Compact style={{ width: "100%" }}>
                      <Form.Item name={"countryCode"} noStyle>
                        <Select
                          virtual={false}
                          showSearch
                          placeholder="Select Contry"
                          defaultValue={"+91"}
                          filterOption={keyFilterOption}
                          className={
                            isPrimaryMobileNumberVerified ? `verified-border` : `pending-border`
                          }
                          disabled={isFormDisabled || isPrimaryMobileNumberDisabled}
                          style={{
                            maxWidth: "35%",
                          }}
                        >
                          {countryList.map((country) => {
                            return (
                              <Option
                                key={country.name}
                                value={country.phoneCode}
                                label={country.phoneCode}
                              >
                                <Flex justify="start" align="center" gap={"5px"}>
                                  <img
                                    src={country.flag}
                                    height={30}
                                    width={30}
                                    className={
                                      (isFormDisabled || isPrimaryMobileNumberDisabled) &&
                                      "disabled-image"
                                    }
                                  />
                                  {window.innerWidth > 425 ? (
                                    <span>{country.phoneCode}</span>
                                  ) : (
                                    <></>
                                  )}
                                </Flex>
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="number"
                        noStyle
                        rules={[
                          {
                            min: 7,
                            max: 14,
                            required: true,
                            message: "Please enter a valid WhatsApp registered number.",
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          style={{
                            width: "80%",
                          }}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          disabled={isFormDisabled || isPrimaryMobileNumberDisabled ? true : false}
                          placeholder="Enter your mobile number"
                          className={
                            isPrimaryMobileNumberVerified ? `verified-border` : `pending-border`
                          }
                          onChange={(e) =>
                            handleMobileNumberChange(e.target.value, {
                              value: form.getFieldValue("countryCode"),
                            })
                          }
                        />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                  {!isPrimaryMobileNumberVerified && (
                    <div className="unverified" align="end">
                      {/* Verification Pending!{" "} */}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            verifyPrimaryEmailOrMobileNumber(primaryMobileNumber, "mobileNumber");
                          }
                        }}
                        className={isFormDisabled && "disabled"}
                      >
                        Verify Now
                      </span>
                    </div>
                  )}
                  {isPrimaryMobileNumberVerified && isPrimaryMobileNumberDisabled && (
                    <div className="verified" align="end">
                      {/* Verified{" "} */}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryMobileNumberDisabled(false);
                          }
                        }}
                        className={isFormDisabled && "disabled"}
                      >
                        Edit Primary Mobile Number
                      </span>
                    </div>
                  )}
                  {isPrimaryMobileNumberVerified && !isPrimaryMobileNumberDisabled && (
                    <div className="verified" align="end">
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryMobileNumberDisabled(true);
                          }
                        }}
                      >
                        Cancel
                      </span>{" "}
                      <span
                        onClick={() => {
                          if (!isFormDisabled) {
                            setIsPrimaryMobileNumberDisabled(false);
                            verifyPrimaryEmailOrMobileNumber(primaryMobileNumber, "mobileNumber");
                          }
                        }}
                      >
                        Verify
                      </span>
                    </div>
                  )}
                  <Form.List name="mobiles">
                    {(fields, { add, name, remove }, { errors }) => (
                      <>
                        {fields.map((field, index) => (
                          <Form.Item
                            {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                            key={field.key}
                          >
                            <div className="dynamic-input">
                              <Form.Item
                                style={{
                                  width: isFormDisabled ? "100%" : "90%",
                                  marginBottom: "0",
                                }}
                              >
                                <Space.Compact style={{ width: "100%" }}>
                                  <Form.Item name={[field.name, "countryCode"]} noStyle>
                                    <Select
                                      virtual={false}
                                      showSearch
                                      placeholder="Select Country"
                                      defaultValue={"+91"}
                                      filterOption={keyFilterOption}
                                      disabled={isFormDisabled}
                                      style={{
                                        maxWidth: "35%",
                                      }}
                                    >
                                      {countryList.map((country) => {
                                        return (
                                          <Option
                                            key={country.name}
                                            value={country.phoneCode}
                                            label={country.phoneCode}
                                          >
                                            <Flex justify="start" align="center" gap={"5px"}>
                                              <img
                                                src={country.flag}
                                                height={30}
                                                width={30}
                                                className={isFormDisabled && "disabled-image"}
                                              />{" "}
                                              {window.innerWidth > 425 ? (
                                                <span>{country.phoneCode}</span>
                                              ) : (
                                                <></>
                                              )}
                                            </Flex>
                                          </Option>
                                        );
                                      })}
                                    </Select>
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, "number"]}
                                    validateTrigger={["onChange", "onBlur"]}
                                    noStyle
                                    rules={[
                                      {
                                        min: 7,
                                        max: 14,
                                        required: true,
                                        message: "Please provide a valid mobile number!",
                                      },
                                    ]}
                                  >
                                    <Input
                                      type="number"
                                      autoComplete="off"
                                      style={{
                                        width: "80%",
                                      }}
                                      onKeyPress={(event) => {
                                        if (!/[0-9]/.test(event.key)) {
                                          event.preventDefault();
                                        }
                                      }}
                                      disabled={isFormDisabled}
                                      placeholder="Enter your mobile number"
                                    />
                                  </Form.Item>
                                </Space.Compact>
                              </Form.Item>
                              {fields.length > 0 ? (
                                <Fragment>
                                  {!isFormDisabled && (
                                    <IconDelete
                                      className="dynamic-delete-button"
                                      onClick={() => remove(field.name)}
                                      color={isFormDisabled ? "#6d6e75" : "#E9635E"}
                                      style={{
                                        border: "1px solid",
                                        borderRadius: "4px",
                                        padding: "4px",
                                        cursor: `${isFormDisabled ? "" : "pointer"}`,
                                      }}
                                    />
                                  )}
                                </Fragment>
                              ) : null}
                            </div>
                          </Form.Item>
                        ))}
                        <Form.Item>
                          <ButtonComponent
                            onClick={() => add()}
                            iconRight={false}
                            disabled={isFormDisabled}
                            label="Add Another Phone"
                            style={{
                              width: "100%",
                              height: "2.5rem",
                              padding: "0.5rem 0",
                              border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                            }}
                          >
                            <IconAdd
                              width="24px"
                              height="24px"
                              color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                              style={{
                                border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                borderRadius: "4px",
                              }}
                            />
                          </ButtonComponent>
                          <Form.ErrorList errors={errors} />
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Gender"
                    name="gender"
                    rules={[
                      {
                        required: true,
                        message: "Please select gender",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      placeholder="Select a gender"
                      className="w-100"
                      onChange={(value) => handleChange(value, "gender")}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {genderOptions.map((gender) => {
                        return (
                          <Option key={gender.value} value={gender.value} label={gender.label}>
                            {gender.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Citizenship"
                    name="nationality"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your citizenship!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Enter your citizenship"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryData?.map((country) => {
                        return (
                          <Option key={country.name} value={country.iso2} label={country.name}>
                            <div className="flex justify-center gap-5">{country.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                    {/* <Input
                      autoComplete="off"
                      disabled={isFormDisabled}
                      placeholder="Enter your citizenship"
                    /> */}
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Country of Birth"
                    name="cob"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your Birth country!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your country of birth"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryData?.map((country) => {
                        return (
                          <Option key={country.name} value={country.iso2} label={country.name}>
                            <div className="flex justify-center gap-5">{country.name}</div>
                          </Option>
                        );
                      })}
                    </Select>
                    {/* <Input
                      disabled={isFormDisabled}
                      autoComplete="off"
                      placeholder="Select your country of birth"
                    /> */}
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Native Language"
                    name="nativeLanguage"
                    rules={[
                      {
                        required: true,
                        message: "Please select your native language",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      mode="multiple"
                      placeholder="Select your native language!"
                      className="w-100 multiSelect"
                      defaultBg={"#fff5f0"}
                      defaultColor={"#DB5800"}
                      optionFilterProp="label"
                      tagRender={tagRender}
                      options={launguageList}
                      disabled={isFormDisabled}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {!isFormDisabled && (
                <Row justify="center">
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <ButtonComponent
                      label="Save Basic Profile Information"
                      varient="primary"
                      htmlType="submit"
                      className="w-100"
                    />
                  </Col>
                </Row>
              )}
            </Form>
            {isFormDisabled && (
              <Row justify="center">
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <ButtonComponent
                    label="Edit Basic Profile Information"
                    varient="primary"
                    htmlType="button"
                    onClick={() => setIsFormDisabled(false)}
                    className="w-100"
                  />
                </Col>
              </Row>
            )}

            <Divider />

            <Row>
              <Col xxl={24}>
                <div className="heading">
                  <div className="heading-text title-h5-1">Advance Contact Information</div>
                </div>
              </Col>
            </Row>

            <Form
              form={passportform}
              name="passportNumber"
              layout="vertical"
              requiredMark={customizeRequiredMark}
            >
              <Row gutter={[16, 0]}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Name as Appears in Passport" name="passportName">
                    <Input placeholder="Enter your passport name" disabled />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Passport Issue Location" name="passportIssueLocation">
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your passport issue location"
                      className="w-100"
                      filterOption={filterOption}
                      disabled
                    >
                      {countryData.map((country) => (
                        <Option key={country.name} value={country.iso2} label={country.name}>
                          <div className="flex justify-center gap-5">{country.name}</div>
                        </Option>
                      ))}
                    </Select>
                    {/* <Input
                      placeholder="Enter your passport issue location"
                      disabled
                    /> */}
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Passport Number" name="passportNumber">
                    <Input placeholder="Enter your passport number" disabled />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Passport Issue Date" name="passportIssueDate">
                    <DatePicker
                      placeholder="Select your passport issue date"
                      disabled
                      format={dateFormatList}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Passport Expiry Date" name="passportExpiryDate">
                    <DatePicker
                      placeholder="Select your passport expiry date"
                      disabled
                      format={dateFormatList}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <div className="more-advance-info body-b1-2">and more advance info....</div>
                </Col>
              </Row>
              <Row justify="center">
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <ButtonComponent
                    label="Edit Advance Information"
                    htmlType="submit"
                    className="w-100"
                    varient="primary"
                    onClick={() => navigate("/profile/advance-contact-information")}
                  />
                </Col>
              </Row>
            </Form>
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default BasicProfileInformation;
